import {
  ContainerController,
  StackLayout,
  ResponsiveLayout,
  ButtonDirection as BirdButtonDirection,
} from '@sas-dvr/ltjs-bird';
import { merge, from, Observable, EMPTY, of } from 'rxjs';
import { BIRDModelControllerLoader } from './LoaderFactory';
import { loadPositionedModelOwnerController } from './loadPositionedModelOwner';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { ContainerState } from '@sas-dvr/internal-va-react-core/store/birdElement/containerState';
import {
  StackContainerState,
  StackContainerData,
  ButtonDirection,
} from '@sas-dvr/internal-va-react-core/store/birdElement/stackContainerState';
import {
  ResponsiveLayoutOverflowType,
  ButtonType,
  HorizontalPosition,
  VerticalPosition,
} from '@sas-dvr/ltjs-commons';
import { getTitleObservables } from '../util/birdRichText';
import { withRetained } from '../util';
import { getModel } from '../util/controllerUtils';
import { AnyAction } from 'redux';
import { map, distinctUntilChanged, tap, ignoreElements } from 'rxjs/operators';

export const loadContainerController: BIRDModelControllerLoader = (
  controller,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(controller instanceof ContainerController)) {
    throw new Error('Expected the controller to be a ContainerController');
  }

  const key = deps.getKey(controller);
  const model = controller.getModel();
  if (!model) {
    throw new Error('Expected the ContainerController to have a Model');
  }

  const addActions: AnyAction[] = [];
  let lifecyleActions;
  const layout = model.getLayout();
  const visualElementController = controller.getVisualContainerElementController();
  let handleStackIndexChange$: Observable<AnyAction> = EMPTY;
  const isStackContainer =
    layout instanceof StackLayout ||
    (layout instanceof ResponsiveLayout &&
      layout.getOverflow() === ResponsiveLayoutOverflowType.STACK);
  if (isStackContainer) {
    const labels: string[] = [];
    const children = controller.getOrderedVisualAndContainerControllers();
    if (children) {
      for (const child of children) {
        const model = getModel(child);
        if (!model) {
          continue;
        }
        labels.push(model.getLabel() || '');
      }
    }
    let type = ButtonType.BUTTON;
    let horizontalPosition = HorizontalPosition.CENTER as StackContainerData['horizontalPosition'];
    let verticalPosition = VerticalPosition.MIDDLE as StackContainerData['verticalPosition'];
    let direction = BirdButtonDirection.VERTICAL;

    if (visualElementController) {
      const stackModel = visualElementController.getModel();
      if (stackModel) {
        const details = stackModel.getStackNavigationControl();
        if (details) {
          direction = details.getButtonDirection();
          horizontalPosition = details.getHorizontalPosition();
          verticalPosition = details.getVerticalPosition();
          type = details.getButtonType();
        }
      }
    }

    lifecyleActions = {
      create: () =>
        StackContainerState.actions.add(key, {
          buttonType: type,
          horizontalPosition: horizontalPosition,
          verticalPosition: verticalPosition,
          direction: convertButtonDirection(direction),
          labels: labels,
          selectedIndex: controller.getStackActiveVisualIndex(),
        }),
      delete: () => StackContainerState.actions.delete(key),
    };

    handleStackIndexChange$ = state$.pipe(
      map((aState) => {
        const stackContainerState = aState.birdElements.StackContainer[key];
        if (!stackContainerState) {
          return undefined;
        }
        return stackContainerState.selectedIndex;
      }),
      distinctUntilChanged(),
      tap((selectedIndex) => {
        if (selectedIndex !== undefined) {
          controller.setStackActiveVisualIndex(selectedIndex);
        }
      }),
      ignoreElements()
    );
  } else {
    lifecyleActions = {
      create: () => ContainerState.actions.add(key, {}),
      delete: () => ContainerState.actions.delete(key),
    };
  }

  let loadElement$: Observable<AnyAction> = EMPTY;
  if (visualElementController) {
    const loader = deps.getLoaderForVisualElementController(visualElementController);
    loadElement$ = loader(
      controller,
      visualElementController,
      unload$,
      myActions$,
      state$,
      deps,
      lifecyleActions
    ).pipe(withRetained(visualElementController));
  } else {
    // TODO: Are there any cases where a container has no VisualElementController?
    loadElement$ = merge(
      of(lifecyleActions.create()),
      of(BirdLayoutState.actions.set(key, deps.getLayoutData(controller))),
      unload$.pipe(map(lifecyleActions.delete))
    );
  }

  const loadChildren$ = loadPositionedModelOwnerController(
    controller,
    unload$,
    myActions$,
    state$,
    deps
  );
  const addContainerElement$ = from(addActions);
  const rc = merge(
    loadChildren$,
    loadElement$,
    addContainerElement$,
    handleStackIndexChange$,
    ...getTitleObservables(key, controller, visualElementController, state$)
  );
  if (visualElementController) {
    return rc.pipe(withRetained(visualElementController));
  }
  return rc;
};

function convertButtonDirection(buttonDirection: BirdButtonDirection): ButtonDirection {
  switch (buttonDirection) {
    case BirdButtonDirection.VERTICAL:
      return 'vertical';
    case BirdButtonDirection.HORIZONTAL:
    default:
      return 'horizontal';
  }
}
